import { FC } from 'react'
import { Box, Chip, Typography } from '@mui/material'
import { Bill, MonthlyBill } from 'src/services'

export const NOTE_ROW_DIVIDER_INDENT_PX = 30
export const BILL_TABLE_ROW_HEIGHT = 42
export const NOTE_ROW_DIVIDER_COLOR = 'rgba(0, 0, 0, 0.05)'
const TREE_LINE_LEFT_PX = 10
const TREE_BRANCH_WIDTH_PX = 12
const TREE_LINE_COLOR = 'rgba(0, 0, 0, 0.12)'

export type BillTreeLinkRole = 'invParent' | 'noteMiddle' | 'noteLast'

export interface BillTreeLink {
  role: BillTreeLinkRole
}

export function isGstBillRow(row: MonthlyBill, account?: Bill): boolean {
  if (row.isGst === true) {
    return true
  }
  if (row.isGst === false) {
    return false
  }
  const rowIsGst = (row as MonthlyBill & { isGST?: boolean }).isGST
  if (rowIsGst === true) {
    return true
  }
  if (rowIsGst === false) {
    return false
  }
  if (account?.isGSTEnabled === true) {
    return true
  }
  return false
}

export function isDebitCreditNoteRow(row: MonthlyBill): boolean {
  return row.type === 'DBN' || row.type === 'CRN'
}

export function isCancelledBillRow(row: MonthlyBill): boolean {
  return row.status === 'CANCELLED'
}

export function getGenerateEInvoiceTooltip(billType: MonthlyBill['type']): string {
  if (billType === 'DBN') {
    return 'Generate e-Debit Note'
  }
  if (billType === 'CRN') {
    return 'Generate e-Credit Note'
  }
  return 'Generate e-Invoice'
}

function isNoteBillRow(bill: MonthlyBill): boolean {
  return bill.id > 0 && isDebitCreditNoteRow(bill)
}

function isInvBillRow(bill: MonthlyBill): boolean {
  return bill.id > 0 && !isDebitCreditNoteRow(bill)
}

export function buildBillTreeLinkMap(bills: MonthlyBill[]): Map<number, BillTreeLink> {
  const map = new Map<number, BillTreeLink>()

  for (let i = 0; i < bills.length; i++) {
    const bill = bills[i]
    if (bill.id < 0 || !isInvBillRow(bill)) {
      continue
    }

    const noteIndices: number[] = []
    let j = i + 1
    while (j < bills.length && isNoteBillRow(bills[j])) {
      noteIndices.push(j)
      j += 1
    }

    if (noteIndices.length === 0) {
      continue
    }

    map.set(bill.id, { role: 'invParent' })
    noteIndices.forEach((noteIndex, index) => {
      map.set(bills[noteIndex].id, {
        role: index === noteIndices.length - 1 ? 'noteLast' : 'noteMiddle',
      })
    })
    i = j - 1
  }

  return map
}

const invParentDottedStemSx = {
  position: 'absolute',
  left: `${TREE_LINE_LEFT_PX}px`,
  top: 'calc(50% + 0.5em)',
  bottom: 0,
  width: '2px',
  backgroundImage: `radial-gradient(circle, ${TREE_LINE_COLOR} 1.25px, transparent 1.25px)`,
  backgroundSize: '2px 7px',
  backgroundRepeat: 'repeat-y',
  pointerEvents: 'none',
} as const

function getTreeConnectorSx(role: BillTreeLinkRole) {
  const verticalLine = {
    content: '""',
    position: 'absolute' as const,
    left: `${TREE_LINE_LEFT_PX}px`,
    width: '1px',
    backgroundColor: TREE_LINE_COLOR,
    pointerEvents: 'none' as const,
  }

  const horizontalBranch = {
    content: '""',
    position: 'absolute' as const,
    left: `${TREE_LINE_LEFT_PX}px`,
    top: '50%',
    width: `${TREE_BRANCH_WIDTH_PX}px`,
    height: '1px',
    backgroundColor: TREE_LINE_COLOR,
    pointerEvents: 'none' as const,
  }

  if (role === 'noteMiddle') {
    return {
      '&::before': {
        ...verticalLine,
        top: 0,
        bottom: 0,
      },
      '&::after': horizontalBranch,
    }
  }

  return {
    '&::before': {
      ...verticalLine,
      top: 0,
      height: '50%',
    },
    '&::after': horizontalBranch,
  }
}

const intervalNameCellWrapperSx = {
  position: 'relative',
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
} as const

const treeLinkCellSx = {
  position: 'relative',
  pl: `${NOTE_ROW_DIVIDER_INDENT_PX}px`,
  ...intervalNameCellWrapperSx,
} as const

const NOTE_TYPE_TAG_STYLES = {
  DBN: {
    backgroundColor: 'rgba(47, 168, 102, 0.14)',
    color: '#249B5B',
  },
  CRN: {
    backgroundColor: 'rgba(216, 75, 106, 0.14)',
    color: '#C94A67',
  },
} as const

export function getBillRowClassName(row: MonthlyBill): string {
  const classes: string[] = []

  if (row.id < 0) {
    classes.push('monthly-bill-row-summary')
  } else if (row.type === 'DBN' || row.type === 'CRN') {
    classes.push('monthly-bill-row-note')
  } else {
    classes.push('monthly-bill-row-inv')
  }

  if (row.status === 'CANCELLED') {
    classes.push('monthly-bill-row-cancelled')
  }

  return classes.join(' ')
}

/** Row styling for MUI Table (Invoice Register) — mirrors DataGrid rules in MonthlyBills */
export const billTableRowWrapperSx = {
  '& thead tr td, & thead tr th': {
    borderTop: 'none',
  },
  '& tbody tr': {
    height: BILL_TABLE_ROW_HEIGHT,
  },
  '& tbody td': {
    height: BILL_TABLE_ROW_HEIGHT,
    py: 0.25,
    px: 1,
    verticalAlign: 'middle',
    borderBottom: 'none',
    boxSizing: 'border-box',
    userSelect: 'none',
  },
  '& tr.monthly-bill-row-inv td, & tr.monthly-bill-row-summary td': {
    borderTop: 'none',
    boxShadow: (theme) => `inset 0 1px 0 ${theme.palette.divider}`,
  },
  '& tr.monthly-bill-row-note td:not(:first-of-type)': {
    borderTop: 'none',
    boxShadow: `inset 0 1px 0 ${NOTE_ROW_DIVIDER_COLOR}`,
  },
  '& tr.monthly-bill-row-note td:first-of-type': {
    borderTop: 'none',
    boxShadow: 'none',
    backgroundImage: `linear-gradient(to right, transparent ${NOTE_ROW_DIVIDER_INDENT_PX}px, ${NOTE_ROW_DIVIDER_COLOR} ${NOTE_ROW_DIVIDER_INDENT_PX}px)`,
    backgroundSize: '100% 1px',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top',
  },
  '& tbody tr:first-of-type td': {
    borderTop: 'none',
    boxShadow: 'none',
    backgroundImage: 'none',
  },
  '& tr.monthly-bill-row-cancelled td': {
    textDecoration: 'line-through',
    color: 'text.secondary',
  },
  '& td.month-cell': {
    position: 'relative',
    overflow: 'visible',
    verticalAlign: 'middle',
  },
}

export const IntervalNameCell: FC<{
  row: MonthlyBill
  link?: BillTreeLink
}> = ({ row, link }) => {
  const label = row.intervalName
  const dataCy = `id_typo_bill_${label}_intervalName`

  if (
    row.id > 0 &&
    (row.type === 'DBN' || row.type === 'CRN')
  ) {
    const tagStyle = NOTE_TYPE_TAG_STYLES[row.type]
    return (
      <Box
        sx={{
          ...treeLinkCellSx,
          ...(link ? getTreeConnectorSx(link.role) : {}),
        }}
        data-cy={dataCy}
      >
        <Chip
          size="small"
          label={label}
          sx={{
            height: 22,
            width: 84,
            borderRadius: '9999px',
            backgroundColor: tagStyle.backgroundColor,
            color: tagStyle.color,
            fontWeight: 700,
            border: 'none',
            boxShadow: 'none',
            '& .MuiChip-label': {
              px: 1,
              fontSize: '0.570rem',
              lineHeight: 1.05,
              letterSpacing: '0.04em',
            },
          }}
        />
      </Box>
    )
  }

  if (link?.role === 'invParent') {
    return (
      <Box
        sx={{
          position: 'relative',
          ...intervalNameCellWrapperSx,
        }}
        data-cy={dataCy}
      >
        <Typography fontWeight="400" component="span">
          {label}
        </Typography>
        <Box sx={invParentDottedStemSx} aria-hidden />
      </Box>
    )
  }

  return (
    <Box sx={intervalNameCellWrapperSx} data-cy={dataCy}>
      <Typography fontWeight={row.id < 0 ? 'bold' : '400'}>
        {label}
      </Typography>
    </Box>
  )
}
